// Location: src/components/admin/analytics/components/EmailLeadsCard.tsx
// Purpose: Reusable component for displaying email leads with pagination
// Scope: Used in affiliate analytics views

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface EmailLead {
	_id: string;
	conversionType: string;
	conversionEmail?: string;
	conversionDate: string;
	utmSource?: string;
	utmCampaign?: string;
	landingPage: string;
	latestAttributionUpdate?: string;
	latestAttributionGameId?: string;
}

interface EmailLeadsCardProps {
	affiliateAnalytics: any | undefined;
	formatNumber: (num: number | undefined) => string;
}

export function EmailLeadsCard({
	affiliateAnalytics,
	formatNumber,
}: EmailLeadsCardProps) {
	const [currentPage, setCurrentPage] = useState(1);
	const [isAnimating, setIsAnimating] = useState(false);
	const itemsPerPage = 10;

	// Filter leads from recent conversions
	const emailLeads = useMemo(() => {
		if (!affiliateAnalytics?.recentConversions) return [];
		return affiliateAnalytics.recentConversions.filter(
			(conversion: EmailLead) => conversion.conversionType === 'email_signup'
		);
	}, [affiliateAnalytics]);

	const totalPages = Math.ceil(emailLeads.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentPageData = emailLeads.slice(startIndex, endIndex);

	const handlePageChange = async (newPage: number) => {
		if (newPage === currentPage || isAnimating) return;

		setIsAnimating(true);
		await new Promise((resolve) => setTimeout(resolve, 150)); // Short delay for animation
		setCurrentPage(newPage);
		setTimeout(() => setIsAnimating(false), 150);
	};

	return (
		<Card className='bg-gradient-to-br from-emerald-500/5 to-green-500/10 border-emerald-500/20'>
			<CardHeader>
				<CardTitle className='flex items-center justify-between'>
					<div className='flex items-center gap-2'>
						<Mail className='h-5 w-5 text-emerald-500' />
						LEADS
					</div>
					<Badge
						variant='secondary'
						className='bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
					>
						{formatNumber(emailLeads.length)} total
					</Badge>
				</CardTitle>
			</CardHeader>
			<CardContent>
				{emailLeads.length > 0 ? (
					<div className='space-y-4'>
						{/* LEADS list with animation */}
						<div
							className={cn(
								'space-y-3 transition-all duration-300 ease-in-out',
								isAnimating
									? 'opacity-0 transform translate-y-2'
									: 'opacity-100 transform translate-y-0'
							)}
						>
							{currentPageData.map(
								(lead: EmailLead, index: number) => (
									<div
										key={`${lead._id}-${currentPage}`}
										className={cn(
											'flex items-center justify-between p-4 bg-white/50 dark:bg-gray-900/50 rounded-lg border border-emerald-200/50 dark:border-emerald-800/30 transition-all duration-300 ease-out hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 hover:border-emerald-300/50 hover:shadow-md',
											'animate-in fade-in slide-in-from-bottom-2'
										)}
										style={{
											animationDelay: `${index * 50}ms`,
											animationFillMode: 'both',
										}}
									>
										<div className='flex items-center gap-3'>
											<div className='h-10 w-10 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0'>
												<Mail className='h-4 w-4 text-white' />
											</div>
											<div className='min-w-0'>
												<div className='font-medium text-emerald-700 dark:text-emerald-300 truncate'>
													{lead.conversionEmail ||
														'Email Lead'}
												</div>
												<div className='text-sm text-muted-foreground'>
													{format(
														new Date(
															lead.conversionDate
														),
														'MMM d, yyyy h:mm a'
													)}
												</div>
												{(lead.utmSource ||
													lead.utmCampaign ||
													lead.latestAttributionUpdate) && (
													<div className='flex items-center gap-2 mt-1'>
														{lead.utmSource && (
															<Badge
																variant='outline'
																className='text-xs px-2 py-0 bg-blue-50 text-blue-600 border-blue-200'
															>
																{lead.utmSource}
															</Badge>
														)}
														{lead.utmCampaign && (
															<Badge
																variant='outline'
																className='text-xs px-2 py-0 bg-purple-50 text-purple-600 border-purple-200'
															>
																{
																	lead.utmCampaign
																}
															</Badge>
														)}
														{lead.latestAttributionUpdate &&
															lead.latestAttributionGameId && (
																<Badge
																	variant='outline'
																	className='text-xs px-2 py-0 bg-amber-50 text-amber-600 border-amber-200'
																	title={`Attribution updated ${format(
																		new Date(
																			lead.latestAttributionUpdate
																		),
																		'MMM d, h:mm a'
																	)}`}
																>
																	Updated by{' '}
																	{
																		lead.latestAttributionGameId
																	}
																</Badge>
															)}
													</div>
												)}
											</div>
										</div>
										<div className='text-right flex-shrink-0'>
											<div className='font-bold text-emerald-600 text-sm'>
												10 KARMA
											</div>
											<div className='text-xs text-muted-foreground'>
												Email Reward
											</div>
										</div>
									</div>
								)
							)}
						</div>

						{/* Beautiful Pagination */}
						{totalPages > 1 && (
							<div className='flex items-center justify-between pt-4 border-t border-emerald-200/30'>
								<div className='text-sm text-muted-foreground'>
									Showing {startIndex + 1}-
									{Math.min(endIndex, emailLeads.length)} of{' '}
									{emailLeads.length} leads
								</div>

								<div className='flex items-center gap-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage - 1)
										}
										disabled={
											currentPage === 1 || isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 transition-all duration-200',
											currentPage === 1
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronLeft className='h-4 w-4' />
									</Button>

									{/* Page numbers */}
									<div className='flex items-center gap-1'>
										{Array.from(
											{ length: totalPages },
											(_, i) => i + 1
										).map((pageNum) => {
											const isActive =
												pageNum === currentPage;
											const isNear =
												Math.abs(
													pageNum - currentPage
												) <= 1;
											const isFirst = pageNum === 1;
											const isLast =
												pageNum === totalPages;

											if (
												!isNear &&
												!isFirst &&
												!isLast &&
												totalPages > 5
											) {
												if (
													pageNum ===
														currentPage - 2 ||
													pageNum === currentPage + 2
												) {
													return (
														<span
															key={pageNum}
															className='text-muted-foreground text-sm'
														>
															...
														</span>
													);
												}
												return null;
											}

											return (
												<Button
													key={pageNum}
													variant={
														isActive
															? 'default'
															: 'outline'
													}
													size='sm'
													onClick={() =>
														handlePageChange(
															pageNum
														)
													}
													disabled={isAnimating}
													className={cn(
														'h-8 w-8 p-0 transition-all duration-200',
														isActive
															? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white border-transparent shadow-md hover:shadow-lg'
															: 'border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-sm'
													)}
												>
													{pageNum}
												</Button>
											);
										})}
									</div>

									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage + 1)
										}
										disabled={
											currentPage === totalPages ||
											isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 transition-all duration-200',
											currentPage === totalPages
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronRight className='h-4 w-4' />
									</Button>
								</div>
							</div>
						)}
					</div>
				) : (
					<div className='text-center text-muted-foreground py-12'>
						<Mail className='h-12 w-12 mx-auto mb-4 text-emerald-400/50' />
						<div className='text-lg font-medium mb-2'>
							No LEADS Yet
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}